import { useEffect, useRef } from "react";
import NotSavingBanner from "@/components/mnf/NotSavingBanner";
import { Switch, Route, Router } from "wouter";
import { useHashLocation } from "wouter/use-hash-location";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/not-found";
import { useConvexAuth, useMutation } from "convex/react";
import * as Sentry from "@sentry/react";
import { hydrateFromServer, startAutoSave, stopAutoSave } from "@/lib/snapshotSync";
import { track } from "@/lib/telemetry";
import { setCurrentUserOverride, queries as storeQueries} from "@/lib/store";
import { toast } from "@/hooks/use-toast";
import { api } from "../../convex/_generated/api";

import PhoneShell from "@/components/mnf/PhoneShell";
import CoachOnly from "@/components/mnf/CoachOnly";
import Today from "@/pages/Today";
import Coach from "@/pages/Coach";
import Welcome from "@/pages/Welcome";
import Garden from "@/pages/Garden";
import Move from "@/pages/Move";
import Library from "@/pages/Library";
import Nourish from "@/pages/Nourish";
import RecipeDetail from "@/pages/RecipeDetail";
import Flourish from "@/pages/Flourish";
import Sisters from "@/pages/Sisters";
import Partner from "@/pages/Partner";
import ShareGrace from "@/pages/ShareGrace";
import Onboarding from "@/pages/Onboarding";
import Cycle from "@/pages/Cycle";
import Word from "@/pages/Word";
import Chat from "@/pages/Chat";
import ShareJourney from "@/pages/ShareJourney";
import Gather from "@/pages/Gather";
import Live from "@/pages/Live";
import GoLive from "@/pages/GoLive";
import KileyChat from "@/pages/KileyChat";
import Messages from "@/pages/Messages";
import Macros from "@/pages/Macros";
import CoachMacros from "@/pages/CoachMacros";
import CoachCompliance from "@/pages/CoachCompliance";
import PrayerWall from "@/pages/PrayerWall";
import Together from "@/pages/Together";
import ChallengeDetail from "@/pages/ChallengeDetail";
import WelcomeJourney from "@/pages/WelcomeJourney";
import ProviderExport from "@/pages/ProviderExport";
import GardenWalk from "@/pages/GardenWalk";
import KileysPick from "@/pages/KileysPick";
import Legal from "@/pages/Legal";
import ConsentGate from "@/pages/ConsentGate";
import Settings from "@/pages/Settings";
import PlanEditor from "@/pages/PlanEditor";
import NotificationPermission from "@/pages/NotificationPermission";
import Billing from "@/pages/Billing";
import CoachNotifications from "@/pages/CoachNotifications";
import CoachReview from "@/pages/CoachReview";
import Profile from "@/pages/Profile";
import ProfileEdit from "@/pages/ProfileEdit";
import Sisterhood from "@/pages/Sisterhood";
import SisterhoodPost from "@/pages/SisterhoodPost";
import SisterNetwork from "@/pages/SisterNetwork";
import SisterNetworkIntros from "@/pages/SisterNetworkIntros";
import SignIn from "@/pages/SignIn";
import AuthVerify from "@/pages/AuthVerify";

function AppRouter() {
  return (
    <Switch>
      <Route path="/"><PhoneShell><Today /></PhoneShell></Route>
      <Route path="/coach"><PhoneShell><CoachOnly><Coach /></CoachOnly></PhoneShell></Route>
      <Route path="/coach/compliance"><PhoneShell><CoachOnly><CoachCompliance /></CoachOnly></PhoneShell></Route>
      <Route path="/coach/review"><PhoneShell><CoachOnly><CoachReview /></CoachOnly></PhoneShell></Route>
      <Route path="/provider-export"><PhoneShell hideNav><ProviderExport /></PhoneShell></Route>
      <Route path="/garden-walk"><PhoneShell><GardenWalk /></PhoneShell></Route>
      <Route path="/kileys-pick"><PhoneShell><KileysPick /></PhoneShell></Route>
      <Route path="/legal/:slug"><PhoneShell><Legal /></PhoneShell></Route>
      <Route path="/legal"><PhoneShell><Legal /></PhoneShell></Route>
      <Route path="/consent"><PhoneShell hideNav><ConsentGate /></PhoneShell></Route>
      <Route path="/settings"><PhoneShell><Settings /></PhoneShell></Route>
      <Route path="/plan-editor"><PhoneShell hideNav><PlanEditor /></PhoneShell></Route>
      <Route path="/notifications/permission"><PhoneShell hideNav><NotificationPermission /></PhoneShell></Route>
      <Route path="/billing"><PhoneShell><Billing /></PhoneShell></Route>
      <Route path="/coach/notifications"><PhoneShell><CoachOnly><CoachNotifications /></CoachOnly></PhoneShell></Route>
      <Route path="/profile"><PhoneShell><Profile /></PhoneShell></Route>
      <Route path="/profile/edit"><PhoneShell><ProfileEdit /></PhoneShell></Route>
      <Route path="/welcome"><PhoneShell hideNav><Welcome /></PhoneShell></Route>
      <Route path="/onboarding"><PhoneShell hideNav><Onboarding /></PhoneShell></Route>
      <Route path="/garden"><PhoneShell><Garden /></PhoneShell></Route>
      <Route path="/move"><PhoneShell><Move /></PhoneShell></Route>
      <Route path="/move/library"><PhoneShell><Library /></PhoneShell></Route>
      <Route path="/nourish/:recipeId"><PhoneShell><RecipeDetail /></PhoneShell></Route>
      <Route path="/nourish"><PhoneShell><Nourish /></PhoneShell></Route>
      <Route path="/flourish"><PhoneShell><Flourish /></PhoneShell></Route>
      <Route path="/sisters"><PhoneShell><Sisters /></PhoneShell></Route>
      <Route path="/sisters/partner"><PhoneShell><Partner /></PhoneShell></Route>
      <Route path="/sisters/prayer-wall"><PhoneShell><PrayerWall /></PhoneShell></Route>
      <Route path="/sisterhood"><PhoneShell><Sisterhood /></PhoneShell></Route>
      <Route path="/sisterhood/post/:id"><PhoneShell hideNav><SisterhoodPost /></PhoneShell></Route>
      <Route path="/sister-network"><PhoneShell><SisterNetwork /></PhoneShell></Route>
      <Route path="/sister-network/intros/:id"><PhoneShell hideNav><SisterNetworkIntros /></PhoneShell></Route>
      <Route path="/sister-network/intros"><PhoneShell hideNav><SisterNetworkIntros /></PhoneShell></Route>
      <Route path="/cycle"><PhoneShell><Cycle /></PhoneShell></Route>
      <Route path="/word"><PhoneShell><Word /></PhoneShell></Route>
      <Route path="/share"><PhoneShell hideNav><ShareGrace /></PhoneShell></Route>
      <Route path="/share-journey"><PhoneShell hideNav><ShareJourney /></PhoneShell></Route>
      <Route path="/chat/:slug"><PhoneShell hideNav><Chat /></PhoneShell></Route>
      <Route path="/gather"><PhoneShell><Gather /></PhoneShell></Route>
      <Route path="/live/:id"><PhoneShell hideNav><Live /></PhoneShell></Route>
      <Route path="/go-live"><PhoneShell hideNav><GoLive /></PhoneShell></Route>
      <Route path="/kiley"><PhoneShell hideNav><KileyChat /></PhoneShell></Route>
      <Route path="/messages"><PhoneShell><Messages /></PhoneShell></Route>
      <Route path="/macros"><PhoneShell><Macros /></PhoneShell></Route>
      <Route path="/coach/macros/:menteeId"><PhoneShell hideNav><CoachOnly><CoachMacros /></CoachOnly></PhoneShell></Route>
      <Route path="/together"><PhoneShell><Together /></PhoneShell></Route>
      <Route path="/together/:slug"><PhoneShell><ChallengeDetail /></PhoneShell></Route>
      <Route path="/welcome-journey"><PhoneShell><WelcomeJourney /></PhoneShell></Route>
      <Route path="/signin"><PhoneShell hideNav><SignIn /></PhoneShell></Route>
      <Route path="/auth/verify"><PhoneShell hideNav><AuthVerify /></PhoneShell></Route>
      <Route component={NotFound} />
    </Switch>
  );
}

function AuthBoot() {
  const { isLoading, isAuthenticated } = useConvexAuth();
  const hydratedRef = useRef(false);
  const ensureDomainId = useMutation(api.users.ensureDomainId);
  const migrateLegacyMealLogs = useMutation(api.nutrition.migrateLegacyMealLogs);

  useEffect(() => {
    track("session_start");
  }, []);

  useEffect(() => {
    if (isLoading) return;
    let cancelled = false;
    if (isAuthenticated && !hydratedRef.current) {
      hydratedRef.current = true;
      (async () => {
        const outcome = await hydrateFromServer();
        if (cancelled) return;

        // Only auto-save once we know the server state. On "failed" we do NOT
        // know whether a good snapshot exists, and auto-saving the
        // un-hydrated local store would overwrite her real data with a blank
        // one. hydrateFromServer has already told the user it failed.
        if (outcome === "restored" || outcome === "empty") {
          startAutoSave();
        } else {
          console.error(
            "[AuthBoot] hydrate failed — auto-save disabled to protect existing server data",
          );
          hydratedRef.current = false; // allow a retry on the next auth change
        }

        // Bind the in-memory store's currentUser to the real Convex
        // identity so saves, comments, and ownership comparisons key
        // to the actual signed-in user (not the seeded demo-account shell).
        try {
          const result = await ensureDomainId({});
          if (cancelled) return;
          if (!result) throw new Error("ensureDomainId returned no identity");
          setCurrentUserOverride({
            id: result.id,
            name: result.name,
            tier: result.tier,
            role: result.role,
          });
          // Carry her legacy in-memory meal logs into Convex. /macros used to write
          // to db.mealLogs while Garden appended to nutritionLogs; nutritionLogs is
          // now the single source of truth, so without this her existing meals simply
          // vanish from the page that recorded them. Idempotent server-side via
          // users.mealLogsMigratedAt, so running it on every boot is a no-op after
          // the first. Best-effort: a failure must not block sign-in.
          try {
            const legacy = storeQueries.allLegacyMealLogsForUser(result.id);
            if (legacy.length > 0) {
              await migrateLegacyMealLogs({
                logs: legacy.map((l) => ({
                  date: l.date, block: l.block, kcal: l.kcal,
                  proteinG: l.proteinG, carbsG: l.carbsG, fatG: l.fatG,
                  note: l.note, loggedAt: l.loggedAt,
                })),
              });
            }
          } catch (err) {
            console.error("[AuthBoot] legacy meal-log migration failed", err);
            Sentry.captureException(err, { tags: { subsystem: "authBoot" } });
          }
        } catch (err) {
          // NOT non-fatal. Without an override the store falls back to the
          // seeded demo account, so the sister would read and
          // write another identity's data. Stop auto-save and say so rather
          // than letting her work land on the seed account.
          if (cancelled) return;
          console.error("[AuthBoot] could not resolve identity", err);
          Sentry.captureException(err, { tags: { subsystem: "authBoot" } });
          stopAutoSave();
          setCurrentUserOverride(null);
          toast({
            variant: "destructive",
            title: "Couldn't load your profile",
            description:
              "We signed you in but couldn't load your account. Please reload — your data is safe.",
          });
        }
      })();
    }
    if (!isAuthenticated && hydratedRef.current) {
      hydratedRef.current = false;
      stopAutoSave();
      setCurrentUserOverride(null);
    }
    return () => { cancelled = true; };
  }, [isLoading, isAuthenticated, ensureDomainId]);

  return null;
}

function GatedRoutes() {
  const { isLoading, isAuthenticated } = useConvexAuth();
  if (isLoading) {
    return (
      <PhoneShell hideNav>
        <div className="flex items-center justify-center min-h-screen">
          <p className="mnf-body text-sm" style={{ color: "var(--mnf-tg)" }}>
            Loading your garden...
          </p>
        </div>
      </PhoneShell>
    );
  }

  if (!isAuthenticated) {
    return (
      <Switch>
        <Route path="/auth/verify">
          <PhoneShell hideNav><AuthVerify /></PhoneShell>
        </Route>
        <Route>
          <PhoneShell hideNav><SignIn /></PhoneShell>
        </Route>
      </Switch>
    );
  }

  return <AppRouter />;
}

function CrashScreen() {
  return (
    <div
      className="flex flex-col items-center justify-center min-h-screen px-6 text-center"
      style={{ background: "var(--mnf-bg, #F4F0E8)" }}
    >
      <p className="mnf-serif" style={{ fontSize: 24, color: "var(--mnf-fd)" }}>
        Something tangled in the garden.
      </p>
      <p className="mnf-body italic mt-2" style={{ fontSize: 14, color: "var(--mnf-tg)" }}>
        We've been notified. Refresh to try again.
      </p>
      <button
        onClick={() => window.location.reload()}
        className="mnf-body mt-6 underline"
        style={{ color: "var(--mnf-f)", fontSize: 14 }}
      >
        Refresh
      </button>
    </div>
  );
}

function App() {
  return (
    <Sentry.ErrorBoundary fallback={<CrashScreen />} showDialog={false}>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          {/* OUTSIDE <Router>/<Switch>. Placing this inside Switch broke the
              entire app: wouter renders only the first MATCHING child, this has
              no `path`, so it matched first and rendered null — a white screen
              on every route. Caught in production; rolled back and re-placed. */}
          <NotSavingBanner />
          <AuthBoot />
          <Router hook={useHashLocation}>
            <GatedRoutes />
          </Router>
        </TooltipProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
